import { type FC, useCallback, useEffect, useRef, useState } from "react";

import { Check, Eye, EyeOff, Pencil } from "lucide-react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { enqueueSnackbar } from "notistack";

import { Button } from "@/shared/components/ui/button";
import { DatePicker } from "@/shared/components/ui/date-picker";
import { Input } from "@/shared/components/ui/input";
import SSNInput from "@/shared/components/SSNInput";
import { Label } from "@/shared/components/ui/label";
import {
  Tooltip,
  TooltipContent,
  TooltipInfoTrigger,
  TooltipProvider,
} from "@/shared/components/ui/tooltip";
import { WHY_WE_ASK, WHY_WE_ASK_TITLE } from "@/shared/constants/whyWeAsk";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import { supabase } from "@/shared/lib/supabase";
import { encryptPersonData } from "@/shared/utils/serverEncryption";
import { unwrapExtraction } from "@/shared/utils/unwrapExtraction";
import {
  formatPersonDateForDisplay,
  isValidSSN,
  normalizeFullDate,
} from "@/shared/utils/formatters";
import { splitFullNameParts } from "@/shared/utils/names";
import { sentryUtils } from "@/shared/utils/sentry";
import { useDeceasedUserData } from "@/shared/hooks/useDeceasedUserData";
import { useDeceasedSSNData } from "@/shared/hooks/useDeceasedSSNData";
import { useUserContacts } from "@/shared/hooks/useUserContacts";
import { useCanonicalAddress } from "@/shared/hooks/useCanonicalAddress";
import { AddressPicker } from "@/shared/components/ui/AddressPicker";
import { resolveAddressLink } from "@/shared/utils/resolveAddressLink";
import {
  buildAddress,
  parseAddress,
  toStateAbbreviation,
  EMPTY_ADDRESS,
} from "@/shared/utils/addressUtils";

import { TriageModalStepLayout } from "./TriageModalStepLayout";

import type { DeathCertificateExtraction } from "@/shared/components/document-viewer/DeathCertificateInsights";
import type { AddressParts } from "@/shared/utils/addressUtils";

const MARITAL_STATUS_OPTIONS = ["Single", "Married", "Widowed", "Divorced", "Unknown"] as const;

const nameColumns = (fullName: string) => {
  const { firstName, middleName, lastName } = splitFullNameParts(fullName);
  return lastName
    ? {
        full_name: fullName,
        first_name: firstName,
        middle_name: middleName || null,
        last_name: lastName,
      }
    : { full_name: fullName };
};

interface FieldDef {
  key: string;
  label: string;
  type: "text" | "select" | "ssn" | "date";
}

const FIELDS: FieldDef[] = [
  { key: "fullName", label: "Full legal name", type: "text" },
  { key: "dateOfBirth", label: "Date of birth", type: "date" },
  { key: "dateOfDeath", label: "Date of passing", type: "date" },
  { key: "residence", label: "Last legal residence", type: "text" },
  { key: "ssn", label: "Social Security number", type: "ssn" },
  { key: "maritalStatus", label: "Marital status", type: "select" },
  { key: "survivingSpouse", label: "Surviving spouse", type: "text" },
];

const maskSSN = (ssn: string): string => {
  const digits = ssn.replace(/\D/g, "");
  if (digits.length < 4) return ssn;
  const last4 = digits.slice(-4);
  return `XXX-XX-${last4}`;
};

const formatAddress = (address: string) => {
  const commaIndex = address.indexOf(",");
  if (commaIndex === -1) return address;
  return (
    <>
      {address.slice(0, commaIndex)}
      <br />
      {address.slice(commaIndex + 1).trim()}
    </>
  );
};

interface TriageModalConfirmDetailsStepProps {
  personFirstName: string;
  estateId: number;
  onBack: () => void;
  onContinue: (result: ConfirmDetailsResult) => void;
}

export interface ConfirmDetailsResult {
  fieldsEdited: string[];
  saveFailed: boolean;
}

const EDITED_FIELD_NAMES: Record<string, string> = {
  fullName: "full_name",
  dateOfBirth: "dob",
  dateOfDeath: "dod",
  residence: "residence",
  ssn: "ssn",
  maritalStatus: "marital_status",
  survivingSpouse: "surviving_spouse",
};

export const TriageModalConfirmDetailsStep: FC<TriageModalConfirmDetailsStepProps> = ({
  personFirstName,
  estateId,
  onBack,
  onContinue,
}) => {
  const personName = personFirstName || "the person who passed";
  const queryClient = useQueryClient();

  const whyWeAsk: Partial<Record<string, string>> = {
    ssn: WHY_WE_ASK.decedentSsn,
    maritalStatus: WHY_WE_ASK.maritalStatus,
  };

  const { fullName, personData } = useDeceasedUserData();
  const location = personData?.location ?? "";
  const { deceasedSSN, isLoading: isSsnLoading } = useDeceasedSSNData();
  const { contacts, createContactAsync } = useUserContacts();
  const spouseContact = contacts?.find((c) => c.relationship?.toLowerCase() === "spouse");
  const [isSaving, setIsSaving] = useState(false);

  const {
    data: extraction,
    isFetched: extractionFetched,
    dataUpdatedAt,
  } = useQuery({
    queryKey: ["deathCertExtraction", estateId],
    queryFn: async () => {
      const { data } = await supabase
        .from("estate_documents")
        .select("specific_doc_extraction, document_extraction")
        .eq("estate_id", estateId)
        .eq("document_type", "death_certificate")
        .order("created_at", { ascending: false })
        .limit(1);
      if (!data || data.length === 0) return null;
      const specific = data[0].specific_doc_extraction as DeathCertificateExtraction | null;
      const general = data[0].document_extraction as Record<string, unknown> | null;
      return (
        unwrapExtraction(specific) ?? unwrapExtraction(general as DeathCertificateExtraction | null)
      );
    },
    staleTime: 0,
    refetchOnMount: "always",
    refetchInterval: (query) => {
      if (!query.state.data) return 3000;
      return false;
    },
  });

  const [values, setValues] = useState({
    fullName: "",
    dateOfBirth: "",
    dateOfDeath: "",
    residence: "",
    maritalStatus: "",
    survivingSpouse: "",
  });
  const [ssnVisible, setSsnVisible] = useState(false);
  const [ssnDraft, setSsnDraft] = useState("");
  const [showSsnError, setShowSsnError] = useState(false);
  const isSsnMissing = !isSsnLoading && !deceasedSSN;
  const hasValidSsn = !!deceasedSSN || (isSsnMissing && isValidSSN(ssnDraft));
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [editValue, setEditValue] = useState("");
  const [addressDraft, setAddressDraft] = useState<AddressParts>(EMPTY_ADDRESS);
  const { data: canonicalDomicile, isFetched: isDomicileFetched } = useCanonicalAddress(
    estateId,
    "domicile",
  );

  const marriageInfo = (personData as Record<string, unknown> | null)?.marriage_info as
    | Record<string, string>
    | undefined;

  const seededFrom = useRef<"none" | "estate" | "extraction">("none");
  const hasEdited = useRef(false);
  const editStartValueRef = useRef("");
  const editedFieldsRef = useRef(new Set<string>());

  useEffect(() => {
    const canSeed =
      extractionFetched &&
      (seededFrom.current === "none"
        ? !!extraction || !!personData
        : seededFrom.current === "estate" && !!extraction && !hasEdited.current);
    if (!canSeed) return;

    const extractedName = extraction?.decedent_information?.full_name ?? "";
    const extractedAddress =
      extraction?.decedent_information?.residence_address ??
      extraction?.other_details?.residence_address ??
      "";
    const spouseName =
      extraction?.surviving_spouse?.name ??
      (spouseContact
        ? `${spouseContact.first_name ?? ""} ${spouseContact.last_name ?? ""}`.trim()
        : "");

    seededFrom.current = extraction ? "extraction" : "estate";
    setValues({
      fullName: extractedName || fullName || "",
      dateOfBirth:
        normalizeFullDate(extraction?.decedent_information?.date_of_birth) ||
        normalizeFullDate(personData?.date_of_birth),
      dateOfDeath:
        normalizeFullDate(extraction?.decedent_information?.date_of_death) ||
        normalizeFullDate(personData?.date_of_death),
      residence: extractedAddress || location || "",
      maritalStatus: extraction?.decedent_information?.marital_status || marriageInfo?.status || "",
      survivingSpouse: spouseName || marriageInfo?.spouse_name || "",
    });
  }, [
    extraction,
    extractionFetched,
    fullName,
    location,
    personData,
    spouseContact,
    marriageInfo,
    dataUpdatedAt,
  ]);

  const startEdit = useCallback(
    (key: string) => {
      const currentValue = key === "ssn" ? (deceasedSSN ?? "") : values[key as keyof typeof values];
      setEditingKey(key);
      setEditValue(currentValue);
      editStartValueRef.current = currentValue;
      if (key === "residence") {
        setAddressDraft(
          canonicalDomicile
            ? {
                streetAddress: canonicalDomicile.street1,
                apartment: canonicalDomicile.street2 ?? "",
                city: canonicalDomicile.city ?? "",
                state: toStateAbbreviation(canonicalDomicile.state ?? ""),
                zipCode: canonicalDomicile.zip,
              }
            : parseAddress(currentValue),
        );
      }
    },
    [values, deceasedSSN, canonicalDomicile],
  );

  const saveSSN = useCallback(
    async (ssn: string) => {
      const encrypted = await encryptPersonData(estateId, ssn, "ssn");
      const { error } = await supabase
        .from("estates")
        .update({ ssn_encrypted: encrypted })
        .eq("estate_id", estateId);
      if (error) throw error;
      queryClient.invalidateQueries({ queryKey: ["deceasedSSNData"] });
    },
    [estateId, queryClient],
  );

  const confirmEdit = useCallback(
    async (key: string) => {
      try {
        if (key === "fullName") {
          const { error } = await supabase
            .from("estates")
            .update(nameColumns(editValue.trim()))
            .eq("estate_id", estateId);
          if (error) return;
          setValues((prev) => ({ ...prev, fullName: editValue.trim() }));
          queryClient.invalidateQueries({ queryKey: ["app-user-data"] });
        } else if (key === "dateOfBirth" || key === "dateOfDeath") {
          const column = key === "dateOfBirth" ? "date_of_birth" : "date_of_death";
          const { error } = await supabase
            .from("estates")
            .update({ [column]: editValue || null })
            .eq("estate_id", estateId);
          if (error) return;
          setValues((prev) => ({ ...prev, [key]: editValue }));
          queryClient.invalidateQueries({ queryKey: ["app-user-data"] });
        } else if (key === "residence") {
          const composed = buildAddress(addressDraft).trim();
          const { error } = await supabase
            .from("estates")
            .update({ location: composed || null })
            .eq("estate_id", estateId);
          if (error) return;
          setValues((prev) => ({ ...prev, residence: composed }));
          void resolveAddressLink({
            raw: composed,
            purpose: "domicile",
            entity: { estate_id: estateId },
          }).finally(() => {
            void queryClient.invalidateQueries({ queryKey: ["canonicalAddress"] });
          });
          queryClient.invalidateQueries({ queryKey: ["app-user-data"] });
        } else if (key === "ssn") {
          if (!isValidSSN(editValue)) return;
          await saveSSN(editValue);
        } else if (key === "maritalStatus" || key === "survivingSpouse") {
          const field = key === "maritalStatus" ? "status" : "spouse_name";
          const { error } = await supabase
            .from("estates")
            .update({ marriage_info: { ...marriageInfo, [field]: editValue } })
            .eq("estate_id", estateId);
          if (error) return;
          setValues((prev) => ({ ...prev, [key]: editValue }));
          queryClient.invalidateQueries({ queryKey: ["app-user-data"] });
        }
        const confirmedValue = key === "residence" ? buildAddress(addressDraft).trim() : editValue;
        if (confirmedValue.trim() !== editStartValueRef.current.trim()) {
          editedFieldsRef.current.add(EDITED_FIELD_NAMES[key] ?? key);
        }
        hasEdited.current = true;
        setEditingKey(null);
      } catch {
        // Keep edit open on failure so user can retry
      }
    },
    [editValue, addressDraft, estateId, queryClient, marriageInfo, saveSSN],
  );

  const saveConfirmedDetails = async () => {
    const confirmedName = values.fullName.trim();
    const spouseName = values.survivingSpouse.trim();
    const residenceChanged = !!values.residence && values.residence !== location;

    const { error } = await supabase
      .from("estates")
      .update({
        ...(confirmedName ? nameColumns(confirmedName) : {}),
        ...(values.dateOfBirth ? { date_of_birth: values.dateOfBirth } : {}),
        ...(values.dateOfDeath ? { date_of_death: values.dateOfDeath } : {}),
        ...(residenceChanged ? { location: values.residence } : {}),
        marriage_info: {
          ...marriageInfo,
          ...(values.maritalStatus ? { status: values.maritalStatus } : {}),
          ...(spouseName ? { spouse_name: spouseName } : {}),
        },
      })
      .eq("estate_id", estateId);
    if (error) throw error;

    if (residenceChanged) {
      void resolveAddressLink({
        raw: values.residence,
        purpose: "domicile",
        entity: { estate_id: estateId },
      }).finally(() => {
        void queryClient.invalidateQueries({ queryKey: ["canonicalAddress"] });
      });
    }

    const { count: existingSpouseCount } = spouseName
      ? await supabase
          .from("estate_contacts")
          .select("id", { count: "exact", head: true })
          .eq("estate_id", estateId)
          .ilike("relationship", "spouse")
      : { count: null };

    if (spouseName && !existingSpouseCount) {
      const { firstName, middleName, lastName } = splitFullNameParts(spouseName);
      await createContactAsync({
        first_name: firstName,
        middle_name: middleName || null,
        last_name: lastName || null,
        relationship: "spouse",
      });
    }

    queryClient.invalidateQueries({ queryKey: ["app-user-data"] });
  };

  const saveAndContinue = async () => {
    setIsSaving(true);
    const ssnSaved = await (isSsnMissing ? saveSSN(ssnDraft) : Promise.resolve()).then(
      () => true,
      (error: unknown) => {
        sentryUtils.captureError(error, "Failed to save death certificate SSN");
        enqueueSnackbar("Couldn't save the Social Security Number. Try again.", {
          variant: "error",
        });
        return false;
      },
    );

    if (ssnSaved) {
      if (isSsnMissing) {
        editedFieldsRef.current.add(EDITED_FIELD_NAMES.ssn);
      }
      let saveFailed = false;
      try {
        await saveConfirmedDetails();
      } catch (error) {
        saveFailed = true;
        sentryUtils.captureError(error, "Failed to save confirmed death certificate details");
      } finally {
        setIsSaving(false);
        onContinue({ fieldsEdited: Array.from(editedFieldsRef.current), saveFailed });
      }
    } else {
      setIsSaving(false);
    }
  };

  const handleContinue = () => {
    if (hasValidSsn) {
      void saveAndContinue();
    } else {
      setShowSsnError(true);
    }
  };

  const handleSsnDraftChange = (formatted: string) => {
    setSsnDraft(formatted);
    setShowSsnError(false);
  };

  const getDisplayValue = (key: string): string => {
    if (key === "ssn") return deceasedSSN ?? "";
    return values[key as keyof typeof values] ?? "";
  };

  return (
    <TriageModalStepLayout
      title={`Confirm ${personName}'s details`}
      description={`These come from ${personName}'s death certificate. Confirm they're right once, and they're filled in for you everywhere they're needed.`}
      activeStep={2}
      onBack={onBack}
      actionButton={
        <Button
          variant="brand"
          size="md"
          onClick={handleContinue}
          disabled={isSaving || isSsnLoading || editingKey !== null}
        >
          Continue
        </Button>
      }
    >
      <div>
        {FIELDS.map((field, i) => {
          const isEditing = editingKey === field.key;
          const displayValue = getDisplayValue(field.key);
          const hasValue = !!displayValue;

          return (
            <div key={field.key}>
              {i > 0 && <div className="border-t border-primary" />}
              <div className="flex items-center gap-4 py-5">
                <Label
                  htmlFor={`field-${field.key}`}
                  className="w-[100px] sm:w-[180px] shrink-0 text-sm font-normal text-secondary"
                >
                  {field.label}
                  {whyWeAsk[field.key] && (
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipInfoTrigger
                          aria-label={WHY_WE_ASK_TITLE}
                          className="ml-1 align-middle"
                        />
                        <TooltipContent title={WHY_WE_ASK_TITLE}>
                          {whyWeAsk[field.key]}
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  )}
                </Label>

                <div className="flex flex-1 items-center gap-2 min-w-0">
                  {isEditing ? (
                    field.type === "select" ? (
                      <Select value={editValue} onValueChange={setEditValue}>
                        <SelectTrigger id={`field-${field.key}`} className="w-[240px]">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {MARITAL_STATUS_OPTIONS.map((opt) => (
                            <SelectItem key={opt} value={opt}>
                              {opt}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : field.key === "residence" ? (
                      <div className="w-full max-w-[420px]">
                        <AddressPicker
                          id={`field-${field.key}`}
                          value={addressDraft}
                          onChange={setAddressDraft}
                          labels={{ streetAddress: "Address", apartment: "Apt, suite, etc." }}
                          stateFormat="abbreviation"
                        />
                      </div>
                    ) : field.type === "date" ? (
                      <div className="w-full max-w-[300px]">
                        <DatePicker
                          id={`field-${field.key}`}
                          dateString={editValue || undefined}
                          onChange={() => {}}
                          onDateStringChange={(val) => setEditValue(val || "")}
                          placeholder="MM/DD/YYYY"
                          isBirthDate={field.key === "dateOfBirth"}
                          inputOnly
                        />
                      </div>
                    ) : field.type === "ssn" ? (
                      <div className="w-full max-w-[300px]">
                        <SSNInput
                          valueFormatted={editValue}
                          onChangeFormatted={setEditValue}
                          ssnVisible={ssnVisible}
                          toggleVisible={() => setSsnVisible((prev) => !prev)}
                          isDetailMissing={false}
                        />
                      </div>
                    ) : (
                      <Input
                        id={`field-${field.key}`}
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        className="max-w-[300px]"
                        autoFocus
                        onKeyDown={(e) => {
                          if (e.key === "Enter") confirmEdit(field.key);
                          if (e.key === "Escape") setEditingKey(null);
                        }}
                      />
                    )
                  ) : field.type === "ssn" && isSsnMissing ? (
                    <div className="w-full max-w-[300px]">
                      <SSNInput
                        valueFormatted={ssnDraft}
                        onChangeFormatted={handleSsnDraftChange}
                        ssnVisible={ssnVisible}
                        toggleVisible={() => setSsnVisible((prev) => !prev)}
                        isDetailMissing={showSsnError}
                      />
                    </div>
                  ) : (
                    <>
                      <p className="text-sm font-medium text-primary">
                        {field.type === "ssn" && hasValue
                          ? ssnVisible
                            ? displayValue
                            : maskSSN(displayValue)
                          : field.key === "residence" && hasValue
                            ? formatAddress(displayValue)
                            : field.type === "date"
                              ? formatPersonDateForDisplay(displayValue, {
                                  year: "numeric",
                                  month: "2-digit",
                                  day: "2-digit",
                                })
                              : displayValue || "—"}
                      </p>
                      {field.type === "ssn" && hasValue && (
                        <button
                          type="button"
                          onClick={() => setSsnVisible((prev) => !prev)}
                          className="text-secondary hover:text-primary"
                          aria-label={ssnVisible ? "Hide SSN" : "Show SSN"}
                        >
                          {ssnVisible ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
                        </button>
                      )}
                    </>
                  )}
                </div>

                <div className="shrink-0">
                  {isEditing ? (
                    <button
                      type="button"
                      onClick={() => confirmEdit(field.key)}
                      disabled={field.type === "ssn" && !isValidSSN(editValue)}
                      className="text-brand hover:text-brand/80 disabled:opacity-50"
                      aria-label="Confirm edit"
                    >
                      <Check className="size-5" />
                    </button>
                  ) : field.type === "ssn" && isSsnMissing ? null : (
                    <button
                      type="button"
                      onClick={() => startEdit(field.key)}
                      disabled={field.key === "residence" && !isDomicileFetched}
                      className="text-tertiary hover:text-primary disabled:opacity-50"
                      aria-label={`Edit ${field.label}`}
                    >
                      <Pencil className="size-4" />
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </TriageModalStepLayout>
  );
};
